import { Branch, Franchisee, FranchiseeConfiguration, TaxRate } from '../../api/dealer-api-interface-franchisee';
import { claimIdentity, userSecurity } from '../../../webmodule-common/other/api/user-security';
import {
  ConnectionState,
  GlobalNotificationManager
} from '../../../webmodule-common/other/ui/icons/icon-notification-signal';
import { currentQuoteSupplierProvider } from '../../quotes/data/quoteSupplierProvider';
import { DevelopmentError } from '../../../webmodule-common/other/development-error';
import { displaySupplierTACNotApprovedMsg } from '../../v6config/supplier-services';
import { emptyGuid } from '../../../webmodule-common/other/api/guid';
import { EventNotify, NullPromise } from '../../../webmodule-common/interop/types/misc';
import {
  chooseDefaultBranch,
  filterUsableBranches,
  findUsableBranch,
  hasUsableBranches,
  isBranchUsable,
  pickUsableBranch,
  shouldNotifyForcedBranchSwitch,
  canChangeBranchHidden as userMayChangeBranchHidden
} from './branch-access';
import { flushCache } from '../cache-impl/cache-registry';
import {
  FranchiseeConfigData,
  IFranchiseeDataAdaptor,
  setFranchiseeDataAdaptor
} from '../../franchisee/data/franchisee-data-adaptor';
import { getApiFactory } from '../../api/api-injector';
import { getCurrentUser, setUserClaims } from '../../../webmodule-common/other/api/current-user';
import { getSupplier } from '../quotes/supplier-selector';
import {
  isCynclyStaff,
  isSupplierAgent,
  updateCurrentUserClaims
} from '../../../webmodule-common/other/currentuser-claims';
import { isEmptyOrSpace, validId } from '../../../webmodule-common/other/ui/string-helper-functions';
import { isMultiSupplier } from '../../quotes/quote-service';
import { MultiPromise } from '../../../webmodule-common/other/general/multicast-promise';
import { removeFromArray } from '../../../webmodule-common/other/array-helper';
import { setSupplierReferenceOverride } from './v6/supplier-reference-override';
import { startSessionKeepAlive, stopSessionKeepAlive } from '../../../webmodule-common/other/api/session-keep-alive';

const branchIdKey = 'webmodule-branchid';

export class CurrentUserDataStore implements IFranchiseeDataAdaptor {
  //IFranchiseeDataAdaptor
  loaded = false;
  private _v6OverrideKey?: string;
  private _systemDefaultBranchId: string = emptyGuid;
  private _branches: Branch[] | null = null;
  /** Session default branch id; emptyGuid means none resolved yet / none available. */
  private _defaultBranchId = emptyGuid;
  private _franchiseeConfig: FranchiseeConfiguration | null = null;
  private loadCorePromise = new MultiPromise<void>(async () => await this.performLoadCoreDetails(), false);

  private _connectionStateUnsubscribe: (() => void) | null = null;

  private _franchiseeChangedHandlers: EventNotify[] = [];

  /**
   * Pending one-time notice that the active default branch was auto-switched
   * because it is no longer usable. Not set for manual switches or first login.
   */
  private _pendingForcedBranchSwitch = false;

  // Subscribers must call the returned unsubscribe in their teardown -
  // long-lived listeners would otherwise outlive the components that own them.
  public onFranchiseeChanged(handler: EventNotify): () => void {
    this._franchiseeChangedHandlers.push(handler);
    return () => removeFromArray(handler, this._franchiseeChangedHandlers);
  }

  /**
   * Returns and clears whether the active branch was auto-switched after it
   * became unusable. Callers show at most one notice per event.
   */
  public consumeForcedBranchSwitchNotification(): boolean {
    const pending = this._pendingForcedBranchSwitch;
    this._pendingForcedBranchSwitch = false;
    return pending;
  }

  private fireFranchiseeChanged(): void {
    // Why fire-and-forget? Handlers are UI re-render triggers; awaiting them
    // would serialise renders behind whatever caller mutated franchisee state.
    for (const h of this._franchiseeChangedHandlers) void h();
  }

  // For settings pages that PATCH a few properties and need the local cache
  // to match without paying for a full `loadCoreDetails` round-trip.
  public applyLocalPropertyChanges(props: Record<string, string>): void {
    this.checkLoaded();
    const target = this.franchisee.properties;
    for (const [key, value] of Object.entries(props)) target[key] = value;
    this.fireFranchiseeChanged();
  }

  public get allowAllBranchAccess(): boolean {
    return userSecurity().claimIsTrue(claimIdentity.allBranches);
  }

  /**
   * Whether the current user may work on hidden branches.
   * Cyncly Staff and supplier agents (support path) — not dealer users.
   */
  public get canAccessHiddenBranches(): boolean {
    return isCynclyStaff() || isSupplierAgent();
  }

  /**
   * Whether the current user may toggle a branch's Hidden flag.
   * Cyncly Staff, Staff Admin, or supplier support (supplier claim and/or agent-token PAT).
   */
  public get canChangeBranchHidden(): boolean {
    return userMayChangeBranchHidden(
      isCynclyStaff(),
      userSecurity().claimIsTrue(claimIdentity.admin),
      userSecurity().isSupplier() || isSupplierAgent()
    );
  }

  /** Same as canAccessHiddenBranches; staff/supplier hidden-branch visibility flag for branch helpers. */
  private get canAccessHidden(): boolean {
    return this.canAccessHiddenBranches;
  }

  private _taxRates: TaxRate[] | null = null;

  public get taxRates(): TaxRate[] {
    this.checkLoaded();
    if (!this._taxRates) throw new DevelopmentError('Franchisee rates are null');
    return this._taxRates;
  }

  private _franchisee: Franchisee | null = null;

  public get franchisee(): Franchisee {
    this.checkLoaded();
    if (!this._franchisee) throw new DevelopmentError('Franchisee is null');
    return this._franchisee;
  }

  public get defaultTaxRate(): number {
    if (this._taxRates && this._taxRates.length > 0) return this._taxRates[0].rate;
    return 0;
  }

  public get config(): FranchiseeConfiguration {
    this.checkLoaded();
    if (!this._franchiseeConfig) throw new DevelopmentError('Franchisee is null');
    return this._franchiseeConfig;
  }

  get clientOwnerId() {
    return this.defaultBranch.id;
  }

  public get defaultBranch(): Branch {
    this.checkLoaded();
    const branch = this.lookupUsable(this._defaultBranchId);
    if (!branch) throw new DevelopmentError('Default Branch is null or hidden');
    return branch;
  }

  public set defaultBranch(value: Branch) {
    // Multi-branch users may pin only a usable branch; others keep the system default.
    const b = this.allowAllBranchAccess ? this.lookupUsable(value.id) : null;
    this.internalSetDefaultBranch(b?.id ?? this._systemDefaultBranchId);
  }

  public get quoteSupplierProvider(): string {
    return currentQuoteSupplierProvider;
  }

  public async setV6OverrideKey() {
    // V6 treats only null/'' as "no override". Map emptyGuid (store "no branch")
    // to '' so it is never sent as a V6 lookup key. Quote paths use a real
    // branch id (quote.quoteOwnerId) and do not go through this helper.
    const aKey = validId(this._defaultBranchId) ? this._defaultBranchId : '';
    if (aKey !== this._v6OverrideKey) {
      await setSupplierReferenceOverride(aKey);
      this._v6OverrideKey = aKey;
      return true;
    }
    return false;
  }

  async singleSupplierTACApproved(): Promise<boolean> {
    if (isMultiSupplier()) return true;
    const supplier = await getSupplier();
    if (isEmptyOrSpace(supplier.supplierId)) return false;
    if (!this.supplierTACApproved(supplier.supplierId)) {
      await displaySupplierTACNotApprovedMsg(supplier, true);
      return false;
    }
    return true;
  }

  /** Multi-branch users only: true when the branch is in their usable set. */
  hasBranch(branchId: string): boolean {
    if (!this.allowAllBranchAccess || !this._branches) return false;
    return this._branches.some(b => b.id === branchId && isBranchUsable(b, this.canAccessHidden));
  }

  //IFranchiseeDataAdaptor
  getFranchisee(): Franchisee {
    return this.franchisee;
  }

  /**
   * Returns true if the fabricator workflow is enabled for this dealer.
   * Set once at onboarding by the supplier; cannot be changed afterwards.
   * This is the single source of truth for all fabrication feature gating on the frontend.
   */
  public get isFabricatorMode(): boolean {
    this.checkLoaded();
    return this.franchisee.properties?.['fabricator-mode-enabled'] === 'true';
  }

  public supplierTACApproved(supplierId: string): boolean {
    this.checkLoaded();
    const key = `supplier-${supplierId}-tac`;
    return this.franchisee.properties[key] === 'true';
  }

  async setFranchisee(franchisee: Franchisee): NullPromise<Franchisee> {
    const result = await getApiFactory().franchisee().updateFranchisee({ franchisee: franchisee });
    if (result && franchisee.id === this.franchisee.id) {
      this._franchisee = result.franchisee;
      this.fireFranchiseeChanged();
    }
    return result?.franchisee ?? null;
  }

  getFranchiseeConfig(): FranchiseeConfigData {
    return {
      franchiseeConfiguration: this.config,
      taxRates: this.taxRates
    };
  }

  async setFranchiseeConfig(franchiseeConfig: FranchiseeConfigData): NullPromise<FranchiseeConfigData> {
    const result = await getApiFactory().franchisee().updateFranchiseeConfiguration({
      franchiseeConfiguration: franchiseeConfig.franchiseeConfiguration,
      taxRates: franchiseeConfig.taxRates
    });
    if (result) {
      this._franchiseeConfig = result.franchiseeConfiguration;
      this._taxRates = result.taxRates;
      return {
        franchiseeConfiguration: result.franchiseeConfiguration,
        taxRates: result.taxRates
      };
    }
    return null;
  }

  /**
   * Branches the user may select in switchers and dashboards.
   * The internal full list stays unfiltered so historical name lookup still works.
   */
  getUserBranches(): Branch[] {
    this.checkLoaded();
    return filterUsableBranches(this._branches, this.canAccessHidden);
  }

  /** True when the user has at least one usable branch. */
  hasAvailableBranches(): boolean {
    return hasUsableBranches(this._branches, this.canAccessHidden);
  }

  async getAllBranches(): Promise<Branch[]> {
    return (await getApiFactory().franchisee().getBranches())?.branches ?? [];
  }

  async setBranch(branch: Branch): Promise<Branch | null> {
    const api = getApiFactory().franchisee();
    const branchResult =
      branch.id === emptyGuid
        ? await api.createBranch({
            abbreviation: branch.abbreviation,
            taxRegistrationNumber: branch.taxRegistrationNumber,
            billingAddress: branch.billingAddress,
            name: branch.name,
            physicalAddress: branch.physicalAddress,
            physicalAddressAsDefaultShipping: branch.physicalAddressAsDefaultShipping,
            shippingNotes: branch.shippingNotes,
            id: null
          })
        : await api.updateBranch({
            branch: branch
          });
    if (branchResult && this._branches) {
      const idx = this._branches.findIndex(x => x.id === branchResult.id);

      if (idx > -1) {
        this._branches[idx] = branchResult;
      } else if (this.allowAllBranchAccess) this._branches.push(branchResult);

      // Keep the default branch pointing at a usable entry after local mutations.
      this.syncDefaultBranchAfterChange();

      return branchResult;
    }

    return null;
  }

  public branchById(id: string): Branch {
    if (!this._branches) throw new DevelopmentError('load branches before trying to access them');
    const b = this._branches.find(x => x.id === id);
    if (!b) throw new Error(`Branch ${id} does not exist`);
    return b;
  }

  /**
   * This should only be called from LogOFF
   */
  clear() {
    this.loaded = false;
    this.loadCorePromise.reset();

    this._franchisee = null;
    this._branches = null;
    this._defaultBranchId = emptyGuid;
    this._franchiseeConfig = null;
    this._systemDefaultBranchId = emptyGuid;
    this._taxRates = null;
    this._pendingForcedBranchSwitch = false;
    localStorage.removeItem(branchIdKey);
    //        this._userClaims = null;

    // Unsubscribe from connection state changes
    if (this._connectionStateUnsubscribe) {
      this._connectionStateUnsubscribe();
      this._connectionStateUnsubscribe = null;
    }

    // Stop the session keep-alive timer
    stopSessionKeepAlive();

    flushCache();
  }

  /**
   * This should called so the next await gets new data, without destroying in use data
   */
  requestRefresh() {
    this.loadCorePromise.reset();
  }

  /**
   * forces the cache to refresh
   * @returns a fresh copy of the franchisee
   */
  async reLoadFranchisee(): NullPromise<Franchisee> {
    this.requestRefresh();
    await this.loadCoreDetails();
    return this.franchisee;
  }

  async loadCoreDetailsAfterLogin() {
    await this.loadCorePromise.run();
    this.subscribeToConnectionState();
    startSessionKeepAlive();
  }

  async loadCoreDetails() {
    await this.loadCorePromise.run();
  }

  /**
   * After a local branch mutation, ensure the default is still usable and notify
   * shell subscribers so branch UI refreshes. Network reloads go through
   * `performLoadCoreDetails` instead (avoids a second franchisee-changed fire).
   */
  private syncDefaultBranchAfterChange(): void {
    if (!this.loaded) return;

    const previousDefaultBranchId = this._defaultBranchId;
    // Keep current session default when still usable; otherwise re-resolve from system default.
    this.internalSetDefaultBranch(validId(this._defaultBranchId) ? this._defaultBranchId : this._systemDefaultBranchId);

    if (
      shouldNotifyForcedBranchSwitch(
        previousDefaultBranchId,
        this._defaultBranchId,
        this._branches,
        this.canAccessHidden
      )
    ) {
      this._pendingForcedBranchSwitch = true;
    }

    this.fireFranchiseeChanged();
  }

  /**
   * Reload franchisee core details and branch access from the server
   * (for example after page resume or connection restore).
   */
  async refreshCoreDetailsAndBranchAccess(): Promise<void> {
    if (!getCurrentUser()) return;

    this.requestRefresh();
    await this.loadCoreDetails();
  }

  private subscribeToConnectionState() {
    if (this._connectionStateUnsubscribe) return;

    const manager = GlobalNotificationManager.getInstance();
    this._connectionStateUnsubscribe = manager.onConnectionStateChange(async (newState, previousState) => {
      const wasOffline =
        previousState === ConnectionState.Offline || previousState === ConnectionState.ServiceRestarting;
      if (newState !== ConnectionState.Online || !wasOffline) return;

      console.log('Connection restored - refreshing user claims');
      try {
        await this.refreshCoreDetailsAndBranchAccess();
      } catch (e) {
        if (!getCurrentUser()) {
          console.log('Connection refresh aborted - user logged out');
          return;
        }
        console.error('Error refreshing user claims after connection restored:', e);
      }
    });
  }

  /**
   * Choose and persist the session default branch.
   * Preference order is owned by chooseDefaultBranch (last-selected for multi-branch
   * users, then server default, then first usable).
   */
  configureDefaultBranch(defaultBranchId: string): string {
    const resolved = chooseDefaultBranch({
      branches: this._branches,
      canAccessHidden: this.canAccessHidden,
      allBranchAccess: this.allowAllBranchAccess,
      localPreferredBranchId: localStorage.getItem(branchIdKey),
      serverDefaultBranchId: defaultBranchId
    });

    if (!resolved) {
      localStorage.removeItem(branchIdKey);
      return emptyGuid;
    }

    localStorage.setItem(branchIdKey, resolved.id);
    return resolved.id;
  }

  /** Prefer-id lookup with no fallback — used to chain candidates. */
  private lookupUsable(preferredId?: string | null): Branch | null {
    return findUsableBranch(this._branches, preferredId, this.canAccessHidden);
  }

  /** First sensible usable branch when no preferred id matches. */
  private pickAnyUsable(): Branch | null {
    return pickUsableBranch(this._branches, this.canAccessHidden);
  }

  private internalSetDefaultBranch(id: string) {
    // Asked-for id first; if unusable/unknown fall back to system default, then any usable.
    const b = this.lookupUsable(id) ?? this.lookupUsable(this._systemDefaultBranchId) ?? this.pickAnyUsable();

    if (!b) {
      if (validId(this._defaultBranchId)) {
        this._defaultBranchId = emptyGuid;
        localStorage.removeItem(branchIdKey);
        flushCache();
      }
      return;
    }

    if (b.id === this._defaultBranchId) return;
    this._defaultBranchId = b.id;
    localStorage.setItem(branchIdKey, b.id);
    flushCache();
  }

  private checkLoaded() {
    if (!this.loaded) throw new Error('CurrentUserDataStore has not been loaded');
  }

  private async performLoadCoreDetails() {
    const user = getCurrentUser();
    if (!user) throw new DevelopmentError('Authentication could not be performed');

    const api = getApiFactory().franchisee();
    const result = await api.getActiveUserInformation({
      systemUserId: user.id
    });
    if (result) {
      this._franchisee = result.franchisee;
      this._branches = result.branches;
      this._franchiseeConfig = result.franchiseeConfiguration;
      this._taxRates = result.taxRates;
      this._systemDefaultBranchId = result.defaultBranchId;
      setUserClaims(result.userClaims);
      updateCurrentUserClaims();

      // Resolve a usable default (emptyGuid when none remain).
      const previousDefaultBranchId = this._defaultBranchId;
      this._defaultBranchId = this.configureDefaultBranch(result.defaultBranchId);
      this.loaded = true;

      if (validId(previousDefaultBranchId) && previousDefaultBranchId !== this._defaultBranchId) {
        flushCache();
        // Notify only when an existing session default was auto-changed.
        if (
          shouldNotifyForcedBranchSwitch(
            previousDefaultBranchId,
            this._defaultBranchId,
            this._branches,
            this.canAccessHidden
          )
        ) {
          this._pendingForcedBranchSwitch = true;
        }
      }

      setFranchiseeDataAdaptor(this);
      this.fireFranchiseeChanged();
    }
    // Result is null - this means the API layer already handled the error
    // (e.g., 412 duplicate login shows dialog and logs out via responseHandler).
    // Don't throw - just let the existing error handling complete.
  }
}

export const userDataStore = new CurrentUserDataStore();
